import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Menu, X, Heart, ChevronDown, ChevronRight, Sun, Moon, Store, BookOpen, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';
import { useSettings } from '@/hooks/useProducts';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import QuickEditInline from '@/components/quickedit/QuickEditInline';
import { useTheme } from '@/hooks/useTheme';
import { SITE_SHORT_NAME, IS_RUMIO, IS_LODGING, IS_SINGLE_PROPERTY } from '@/config/site';
import LocaleSwitcher from '@/components/LocaleSwitcher';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAppMounts, type AppManifestNavEntry, type AppMount } from '@/lib/apps-mounts';
import { recallVertical, verticalFromPath, verticalHref } from '@/pages/public/saas/verticals';
import { useActiveMount } from '@/hooks/useActiveMount';
import { useAppNavStore } from '@/stores/app-nav-store';
import { resolveAppNavIcon } from '@/lib/app-nav-icon';
import NotificationBell from '@/components/layout/NotificationBell';
import type { HeaderVariant } from '@/looks';
import { canAccessAdminShell, isDesigner } from '@/lib/roles';
import { DESIGNER_HOME } from '@/config/designer-surface';
import { useSiteSettingMutation } from '@/hooks/useSiteSettingMutation';

/**
 * Where the "Admin panel" link points. A designer sent to /admin would be
 * bounced straight back out by AdminLayout, so point them at a page their role
 * can actually open.
 */
function adminLinkDest(role: string | null | undefined): string {
  return isDesigner(role) ? DESIGNER_HOME : '/admin';
}

/**
 * Unified nav link shape (contract v1.6). The Header maps marketing links,
 * app manifest entries, and runtime-contributed entries into this same shape
 * so the render functions don't need to branch by source.
 */
interface NavLink {
  label: string;
  href?: string;
  axissOpen?: boolean;
  axissView?: 'home' | 'chat';
  hide_when_authed?: boolean;
  badge?: { text: string; tone?: 'neutral' | 'accent' | 'warning' };
  children?: NavLink[];
  /** Icon name from the app manifest / runtime nav contribution (v1.6).
   *  Kebab-case lucide name; unknown values render no glyph. */
  icon?: string;
}

/** Translate a manifest nav entry to a host-shell link, prefixing the path
 *  with the mount's `path_prefix`. When the entry's `path` matches another
 *  mount's `default_path` (e.g. multi-mount apps), we link to that mount
 *  instead so the user lands at the canonical bare prefix. */
function manifestEntryToNavLink(
  entry: AppManifestNavEntry,
  primaryMount: AppMount,
  allMountsForApp: AppMount[],
): NavLink {
  const matched = allMountsForApp.find((m) => m.default_path === entry.path);
  const href = matched
    ? `/${matched.path_prefix}`
    : `/${primaryMount.path_prefix}${entry.path === '/' ? '' : entry.path}`;
  return {
    label: entry.label,
    href,
    badge: entry.badge,
    icon: entry.icon,
    children: entry.children?.map((c) => manifestEntryToNavLink(c, primaryMount, allMountsForApp)),
  };
}

function badgeToneClass(tone: 'neutral' | 'accent' | 'warning' | undefined): string {
  switch (tone) {
    case 'accent':
      return 'bg-terra/15 text-terra-700';
    case 'warning':
      return 'bg-amber-100 text-amber-800';
    case 'neutral':
    default:
      return 'bg-sage-100 text-sage-800';
  }
}

interface HeaderProps {
  /**
   * Render the stripped-down "minimal chrome" variant: logo + Sign In +
   * Play Now CTA only. No search, cart, mobile menu, user menu, or nav.
   * Seeded for social_casino tenants by the wizard; tenants can opt-in via
   * site_settings.layout_chrome = 'minimal'.
   *
   * Equivalent to passing `variant="minimal"`; kept for backwards compatibility
   * with `layout_chrome === 'minimal'` callers.
   */
  minimal?: boolean;
  /**
   * Layout variant from the tenant's selected Look (theme_config.look).
   * - 'centered_logo' (default): logo centered above a horizontal nav row
   * - 'classic':                 logo-left with inline nav next to actions
   * - 'mega_menu':               centered_logo layout with a larger, normal-case nav row
   * - 'transparent_overlay':     centered_logo layout; visual transparency lands in Phase 3
   *                              once paired with a fullscreen_media hero
   * - 'minimal':                 same as the `minimal` prop above
   */
  variant?: HeaderVariant;
}

export default function Header({ minimal: minimalProp = false, variant = 'centered_logo' }: HeaderProps = {}) {
  const minimal = minimalProp || variant === 'minimal';
  const isClassic = variant === 'classic';
  const isMega = variant === 'mega_menu';
  // Lodging on a centered-logo variant: render the nav inline on the LEFT of
  // the logo row (and drop the separate nav row below), so the header reads
  // nav-left · wordmark-center · actions-right. Without a left in-flow element
  // the actions cluster collapses to the left of the absolutely-centered logo.
  const isLodgingCentered = IS_LODGING && !isClassic && !minimal;
  const { t } = useTranslation(['nav', 'common']);
  const { user, logout } = useAuthStore();
  const itemCount = useCartStore((s) => s.itemCount());
  const { isMobileMenuOpen, toggleMobileMenu, closeMobileMenu, openCartDrawer } = useUIStore();
  const { data: settingsData } = useSettings();
  const cartDrawerEnabled = settingsData?.settings?.upsell_config?.cart_drawer?.enabled;
  const announcementText = settingsData?.settings?.announcement_bar_text;
  const announcementEnabled = settingsData?.settings?.announcement_bar_enabled;
  // Default ON — existing sites without the setting persisted should keep
  // rendering the icon. Tenant turns it off via admin → Settings → Header.
  const headerSearchEnabled = settingsData?.settings?.header_search_enabled !== false;
  const { isActive: quickEditActive } = useQuickEdit();
  // One save path for every quick-edit field on this component: the hook
  // owns the cache invalidation, so it cannot be forgotten at a call site.
  const settingMutation = useSiteSettingMutation();
  const saveSetting = (settings: Record<string, unknown>) =>
    settingMutation.mutateAsync(settings);
  const { theme, toggleTheme } = useTheme();
  const { isEnabled } = useFeatureFlags();
  const showEcommerce = isEnabled('ecommerce');
  const showCourses = isEnabled('courses');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);

  // Track scroll for header shadow
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 10);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const storeName = settingsData?.settings?.store_name || SITE_SHORT_NAME;
  // AI-generated (or admin-set) brand logo. Falls back to the Lucide mark if
  // unset or if the image fails to load. Public setting: store_logo_url.
  const [logoOk, setLogoOk] = useState(true);
  const logoUrl = logoOk ? ((settingsData?.settings?.store_logo_url as string | undefined) || undefined) : undefined;
  // Dark-mode variant (white colorway, written by Branding Studio apply).
  // Rendered as a CSS-swapped sibling so no JS theme state is needed; absent
  // -> the light logo serves both modes, exactly as before.
  const logoDarkUrl = logoOk
    ? ((settingsData?.settings?.store_logo_dark_url as string | undefined) || undefined)
    : undefined;

  if (minimal) {
    return (
      <header
        className={cn(
          'sticky top-0 z-40 bg-surface/90 backdrop-blur border-b border-line transition-shadow',
          scrolled && 'shadow-sm',
        )}
        data-minimal-chrome
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center font-bold text-text-primary min-w-0">
            {logoUrl ? (
              <>
                <img
                  src={logoUrl}
                  alt={storeName}
                  onError={() => setLogoOk(false)}
                  className={cn(
                    'relative z-50 h-9 sm:h-10 w-auto max-w-[55vw] object-contain object-left',
                    logoDarkUrl && 'dark:hidden',
                  )}
                />
                {logoDarkUrl && (
                  <img
                    src={logoDarkUrl}
                    alt={storeName}
                    className="relative z-50 hidden h-9 sm:h-10 w-auto max-w-[55vw] object-contain object-left dark:block"
                  />
                )}
              </>
            ) : (
              <span className="truncate text-xl">{storeName}</span>
            )}
          </Link>
          <div className="flex items-center gap-3">
            {!user && (
              <Link
                to="/login"
                className="text-sm text-text-secondary hover:text-text-primary"
              >
                {t('common:signIn', { defaultValue: 'Sign In' })}
              </Link>
            )}
            {/* dark-exempt: intentional dark text on a non-flipping amber chip; reads in both modes */}
            <Link
              to="/play"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-amber-400 text-stone-900 font-semibold text-sm rounded-full hover:bg-amber-300 transition-colors" /* dark-exempt */
            >
              {t('common:playNow', { defaultValue: 'Play Now' })}
            </Link>
          </div>
        </div>
      </header>
    );
  }

  // Lodging tenants get a stays-shaped default nav. In practice the wizard
  // seeds `settings.navigation_links` (Stays / Local Guide / …), so this only
  // fires as a fallback when a lodging site has no seeded nav — but it keeps
  // that fallback on-brand instead of showing Blog/About/Help with no "Stays".
  const defaultNavLinks = IS_LODGING
    ? [
        { label: t('nav:stays', { defaultValue: 'Stays' }), href: '/properties' },
        { label: t('nav:localGuide', { defaultValue: 'Local Guide' }), href: '/page/things-to-do' },
        { label: t('nav:houseRules', { defaultValue: 'House Rules' }), href: '/page/house-rules' },
        { label: t('nav:findReservation', { defaultValue: 'Find My Reservation' }), href: '/reservations' },
        { label: t('nav:help'), axissOpen: true },
      ]
    : [
        ...(showEcommerce ? [{ label: t('nav:shop'), href: '/shop' }] : []),
        ...(showCourses ? [{ label: t('nav:courses'), href: '/courses' }] : []),
        { label: t('nav:blog'), href: '/blog' },
        { label: t('nav:about'), href: '/page/about' },
        { label: t('nav:help'), axissOpen: true },
      ];
  const LogoIcon = showCourses && !showEcommerce ? BookOpen : Store;

  // Apps Platform v1.1+v1.6: pull nav entries declared in installed-app
  // manifests AND runtime nav contributed via `rumio:nav` (v1.6 §3).
  // Multiple mounts of the same app share one manifest; we dedupe by app id.
  // For each entry, we link to the mount whose default_path matches the
  // entry's path; otherwise fall back to the first mount with the entry
  // path appended. Children get the same prefix translation.
  const { data: mounts } = useAppMounts();
  const runtimeNavByAppId = useAppNavStore((s) => s.runtimeNavByAppId);
  const activeMountRes = useActiveMount();

  const mountsByApp = new Map<string, AppMount[]>();
  for (const m of mounts ?? []) {
    const list = mountsByApp.get(m.app.id) ?? [];
    list.push(m);
    mountsByApp.set(m.app.id, list);
  }

  function resolvedNavForApp(mount: AppMount): NavLink[] {
    const all = mountsByApp.get(mount.app.id) ?? [mount];
    const source =
      runtimeNavByAppId[mount.app.id] !== undefined
        ? runtimeNavByAppId[mount.app.id]
        : mount.app.manifest?.nav ?? null;
    if (!source || source.length === 0) return [];
    return source.map((e) => manifestEntryToNavLink(e, mount, all));
  }

  // Append-mode app nav, grouped per-app into a labeled section. Sections
  // surface inside the avatar/account dropdown (desktop) and the mobile
  // drawer's account block, gated on `user.hasActiveSubscription`. The
  // grouping per-app is intentional: a flat row of every mounted app's
  // pages bloats the host marketing nav (see plan: combined-nav).
  interface AppNavSection { appId: string; appName: string; items: NavLink[]; }
  const appSections: AppNavSection[] = [];
  for (const appMounts of mountsByApp.values()) {
    if (!appMounts || appMounts.length === 0) continue;
    const items = resolvedNavForApp(appMounts[0]);
    if (items.length === 0) continue;
    appSections.push({
      appId: appMounts[0].app.id,
      appName: appMounts[0].app.name,
      items,
    });
  }
  // We only surface the section to entitled users — anyone else (anonymous,
  // free tier) gets no app nav at all. Tightening per-app (matching a
  // specific subscription to a specific app) is a follow-up; today's
  // tenants ship one paid app each.
  const visibleAppSections = user?.hasActiveSubscription ? appSections : [];

  const navFromSettings = settingsData?.settings?.navigation_links;
  const configuredNav: NavLink[] = Array.isArray(navFromSettings) ? navFromSettings : defaultNavLinks;
  // rumio.world has a version of Features and Pricing per site type. Once a
  // visitor is inside one (or has picked one before), those two links follow
  // it, so "Pricing" from the lodging pages is lodging pricing. The nav rows
  // themselves stay type-neutral in `navigation_links`.
  const activeVertical = IS_RUMIO
    ? verticalFromPath(location.pathname)?.id ?? recallVertical()
    : null;
  const baseNav: NavLink[] = activeVertical
    ? configuredNav.map((link) => (link.href ? { ...link, href: verticalHref(link.href, activeVertical) } : link))
    : configuredNav;
  // Three ways to hide marketing nav from authed users, from coarsest to
  // finest:
  // 1. `hide_nav_when_authed` site setting — drops EVERY marketing link
  //    from the header (and the mobile drawer's marketing section) when
  //    the user is signed in. App-shell links from installed-app manifests
  //    still render. Use this on SaaS sites whose authed users live
  //    entirely inside an embedded custom-app surface.
  // 2. `hide_when_authed` per-link flag on `navigation_links` — hides
  //    specific marketing links (Pricing, Features, etc.) but leaves
  //    others visible.
  // 3. Nothing — default: every link renders for authed users.
  const hideAllNavWhenAuthed = !!settingsData?.settings?.hide_nav_when_authed;
  const filteredBaseNav = user
    ? hideAllNavWhenAuthed
      ? []
      : baseNav.filter((link) => !link.hide_when_authed)
    : baseNav;

  // Contract v1.6 §2: when inside an app mount with effective nav_mode='replace',
  // we hide the marketing nav since the active app owns the surface.
  const inReplaceMode = !!activeMountRes && activeMountRes.effectiveNavMode === 'replace';
  // Contract v1.10 combined-nav: the active app's pages live in the
  // avatar/account dropdown (desktop) + mobile account block via
  // `visibleAppSections` — NOT in the main nav row. In replace mode the main
  // nav row is empty: the app's pages are in the dropdown, the logo already
  // links home, and the redundant "Back to {site}" affordance was dropped.
  const navLinks: NavLink[] = inReplaceMode ? [] : [...filteredBaseNav];

  // Track which dropdown is open (desktop) and which mobile section is
  // expanded. Single open dropdown at a time — matches the user-menu
  // pattern already used below. We can't use a single ref to detect
  // click-outside because the same nav renders in up to three places
  // (classic inline, desktop row, mobile drawer); instead, dropdown
  // triggers and panels carry a `data-nav-dropdown` attribute so the
  // outside-click handler can recognize any of them.
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileGroups, setOpenMobileGroups] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!openDropdown) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenDropdown(null);
    }
    function onClick(e: MouseEvent) {
      const target = e.target as Element | null;
      if (target?.closest('[data-nav-dropdown]')) return;
      setOpenDropdown(null);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [openDropdown]);

  // Close any open dropdown / mobile group whenever the route changes (e.g.
  // user clicks a child link). Driven by navigate() side-effects.
  useEffect(() => {
    setOpenDropdown(null);
    setOpenMobileGroups(new Set());
  }, [inReplaceMode]);

  function toggleMobileGroup(label: string) {
    setOpenMobileGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  // Close user menu on Escape and trap focus
  useEffect(() => {
    if (!userMenuOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setUserMenuOpen(false);
        userMenuButtonRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [userMenuOpen]);

  // Close mobile menu on Escape
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeMobileMenu();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    // Lock background scroll while the drawer overlay is open.
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen, closeMobileMenu]);

  // Render helpers — defined inline so they close over `t`, the dropdown
  // state, and `closeMobileMenu`. v1.6 dropdowns: parent renders as a
  // button trigger with ChevronDown; clicking opens a panel of children
  // anchored below.
  function renderDesktopLink(link: NavLink, linkClass: string) {
    if (link.axissOpen) {
      return (
        <button
          key={link.label}
          data-axiss-open={link.axissView || 'home'}
          className={cn(linkClass, 'cursor-pointer')}
        >
          {link.label}
        </button>
      );
    }
    if (link.children && link.children.length > 0) {
      const isOpen = openDropdown === link.label;
      return (
        <div key={link.label} className="relative" data-nav-dropdown>
          <button
            type="button"
            aria-expanded={isOpen}
            aria-haspopup="menu"
            onClick={() => setOpenDropdown(isOpen ? null : link.label)}
            className={cn(linkClass, 'inline-flex items-center gap-1 cursor-pointer')}
          >
            {link.label}
            {link.badge && (
              <span className={cn('ml-1 text-[10px] font-semibold rounded-full px-1.5 py-0.5', badgeToneClass(link.badge.tone))}>
                {link.badge.text}
              </span>
            )}
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          </button>
          {isOpen && (
            <div
              role="menu"
              data-nav-dropdown
              className="absolute left-0 top-full mt-1 min-w-[12rem] bg-surface rounded-lg shadow-lg border border-sage-200 z-30 py-1"
            >
              {link.children.map((child) =>
                child.href ? (
                  <Link
                    key={child.href}
                    to={child.href}
                    role="menuitem"
                    onClick={() => setOpenDropdown(null)}
                    className="flex items-center justify-between gap-3 px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none"
                  >
                    <span>{child.label}</span>
                    {child.badge && (
                      <span className={cn('text-[10px] font-semibold rounded-full px-1.5 py-0.5', badgeToneClass(child.badge.tone))}>
                        {child.badge.text}
                      </span>
                    )}
                  </Link>
                ) : null,
              )}
            </div>
          )}
        </div>
      );
    }
    const Icon = resolveAppNavIcon(link.icon);
    return (
      <Link
        key={link.href}
        to={link.href!}
        className={cn(linkClass, 'inline-flex items-center gap-1.5')}
      >
        {Icon && <Icon className="h-4 w-4" aria-hidden />}
        {link.label}
        {link.badge && (
          <span className={cn('text-[10px] font-semibold rounded-full px-1.5 py-0.5', badgeToneClass(link.badge.tone))}>
            {link.badge.text}
          </span>
        )}
      </Link>
    );
  }

  function renderMobileLink(link: NavLink) {
    const baseClass =
      'block w-full text-left px-3 py-3 text-base font-medium text-sage-800 hover:bg-sage-50 rounded-lg min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600';
    if (link.axissOpen) {
      return (
        <button
          key={link.label}
          data-axiss-open={link.axissView || 'home'}
          className={cn(baseClass, 'cursor-pointer flex items-center')}
          onClick={closeMobileMenu}
        >
          {link.label}
        </button>
      );
    }
    if (link.children && link.children.length > 0) {
      const isOpen = openMobileGroups.has(link.label);
      return (
        <div key={link.label}>
          <button
            type="button"
            aria-expanded={isOpen}
            onClick={() => toggleMobileGroup(link.label)}
            className={cn(baseClass, 'flex items-center justify-between')}
          >
            <span className="flex items-center gap-2">
              {link.label}
              {link.badge && (
                <span className={cn('text-[10px] font-semibold rounded-full px-1.5 py-0.5', badgeToneClass(link.badge.tone))}>
                  {link.badge.text}
                </span>
              )}
            </span>
            <ChevronRight className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-90')} aria-hidden="true" />
          </button>
          {isOpen && (
            <div className="pl-4 border-l border-sage-100 ml-3 mt-1 space-y-1">
              {link.children.map((child) =>
                child.href ? (
                  <Link
                    key={child.href}
                    to={child.href}
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                  >
                    {child.label}
                  </Link>
                ) : null,
              )}
            </div>
          )}
        </div>
      );
    }
    const Icon = resolveAppNavIcon(link.icon);
    return (
      <Link
        key={link.href}
        to={link.href!}
        className={cn(baseClass, 'flex items-center gap-2')}
        onClick={closeMobileMenu}
      >
        {link.href === '/' && inReplaceMode && <ArrowLeft className="h-4 w-4" aria-hidden="true" />}
        {Icon && <Icon className="h-4 w-4" aria-hidden />}
        {link.label}
        {link.badge && (
          <span className={cn('text-[10px] font-semibold rounded-full px-1.5 py-0.5', badgeToneClass(link.badge.tone))}>
            {link.badge.text}
          </span>
        )}
      </Link>
    );
  }

  return (
    <>
    <header
      className={cn('sticky top-0 z-40 bg-cream transition-shadow duration-300', scrolled && 'shadow-md')}
      data-header-variant={variant}
      data-scrolled={scrolled ? 'true' : 'false'}
      role="banner"
    >
      {/* Announcement bar */}
      {announcementEnabled && announcementText && (
        <div
          /* Deliberately-dark strip: surface-fixed-dark keeps it dark-brand in
             dark mode instead of inverting bright (sage-800 flips light). */
          className="surface-fixed-dark bg-sage-800 text-sage-100 text-xs text-center py-1.5 px-4 font-display italic tracking-wide-boutique"
          role="status"
          aria-live="polite"
        >
          {quickEditActive ? (
            <span className="inline-flex items-center gap-2">
              <QuickEditInline
                value={announcementText}
                onSave={async (val) => {
                  await saveSetting({ announcement_bar_text: val });
                }}
                label="Announcement bar text"
                as="span"
                className="text-sage-100 font-display italic"
              />
              {/* i18n-exempt: admin-only quick-edit shortcut, not visible to customers */}
              <Link to="/admin/settings/theme" className="ml-1 text-sage-300 hover:text-white underline text-xs not-italic">
                Theme
              </Link>
            </span>
          ) : (
            announcementText
          )}
        </div>
      )}

      {/* Logo row */}
      <div className="bg-cream border-b border-sage-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={cn('flex items-center h-16 lg:h-20', isClassic ? 'justify-between gap-6' : 'justify-between')}>
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 -ml-2 text-sage-800 hover:text-sage-900 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
              onClick={toggleMobileMenu}
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? t('nav:closeMenu') : t('nav:openMenu')}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            {/* Logo —
               classic: left-aligned, no absolute positioning, paired with inline desktop nav
               centered_logo / mega_menu / transparent_overlay: centered (absolute on desktop)
            */}
            {/*
              min-w-0 lets the logo container shrink below its content's
              natural width when the row gets tight — without it the
              actions cluster pushes the row past the viewport on narrow
              phones (LSG's store name + 4 icon buttons overflows ~375px
              viewports), which forces a horizontal page scroll and the
              white gutter to the right of embedded app iframes.
            */}
            <div
              className={cn(
                'min-w-0',
                isClassic
                  ? 'flex justify-start'
                  : 'flex-1 flex justify-center lg:flex-none lg:absolute lg:left-1/2 lg:-translate-x-1/2',
              )}
            >
              <Link
                to="/"
                className="inline-flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded min-w-0"
                onClick={closeMobileMenu}
              >
                {logoUrl ? (
                  // Wordmark logo: replaces the title, rendered big and allowed to
                  // overflow the header band (the header has no overflow-clip).
                  <>
                    <img
                      src={logoUrl}
                      alt={storeName}
                      onError={() => setLogoOk(false)}
                      className={cn(
                        'relative z-50 h-11 sm:h-14 lg:h-16 w-auto max-w-[55vw] sm:max-w-sm lg:max-w-md object-contain object-left',
                        logoDarkUrl && 'dark:hidden',
                      )}
                    />
                    {logoDarkUrl && (
                      <img
                        src={logoDarkUrl}
                        alt={storeName}
                        className="relative z-50 hidden h-11 sm:h-14 lg:h-16 w-auto max-w-[55vw] sm:max-w-sm lg:max-w-md object-contain object-left dark:block"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <LogoIcon className="h-6 w-6 sm:h-7 sm:w-7 text-sage-600 shrink-0" aria-hidden="true" />
                    <span className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-sage-900 truncate">
                      {quickEditActive ? (
                        <QuickEditInline
                          value={storeName}
                          onSave={async (val) => {
                            await saveSetting({ store_name: val });
                          }}
                          label="Store name"
                          as="span"
                          className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-sage-900"
                        />
                      ) : storeName}
                    </span>
                  </>
                )}
              </Link>
            </div>

            {/* Classic-variant inline nav (desktop only) — sits between logo and actions.
                Other variants render the nav in the dedicated row below. */}
            {isClassic && (
              <nav
                className="hidden lg:flex items-center gap-6 flex-1 justify-center"
                aria-label={t('nav:mainNavigation')}
              >
                {navLinks.map((link) =>
                  renderDesktopLink(
                    link,
                    'text-sm font-sans text-sage-800 hover:text-sage-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded px-1 py-1',
                  ),
                )}
              </nav>
            )}

            {/* Lodging centered-logo variant: invisible spacer on the LEFT
                (desktop only) so `justify-between` pushes the actions cluster
                to the right while the logo stays absolutely centered. The real
                nav lives in the dedicated row below — putting the full nav
                inline here collides with the centered wordmark on narrower
                desktops (e.g. "The Juliet" + 5 items at 1024–1200px). */}
            {isLodgingCentered && (
              <div className="hidden lg:block flex-1" aria-hidden="true" />
            )}

            {/* Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              <LocaleSwitcher className="hidden sm:inline-flex" />
              <button
                onClick={toggleTheme}
                className="p-2 text-sage-800 hover:text-sage-900 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                aria-label={theme === 'light' ? t('common:accessibility.darkTheme') : t('common:accessibility.lightTheme')}
              >
                {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </button>

              {headerSearchEnabled && isEnabled('meilisearch') && (
                <Link
                  to="/search"
                  className="p-2 text-sage-800 hover:text-sage-900 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                  aria-label={t('nav:search')}
                >
                  <Search className="h-5 w-5" />
                </Link>
              )}

              {showEcommerce && user && (
                <Link
                  to="/account/wishlist"
                  className="hidden sm:flex p-2 text-sage-800 hover:text-sage-900 min-w-[44px] min-h-[44px] items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                  aria-label={t('nav:wishlist')}
                >
                  <Heart className="h-5 w-5" />
                </Link>
              )}

              {showEcommerce && (cartDrawerEnabled ? (
                <button
                  onClick={openCartDrawer}
                  className="relative p-2 text-sage-800 hover:text-sage-900 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                  aria-label={t('nav:cartWithCount', { count: itemCount })}
                >
                  <ShoppingCart className="h-5 w-5" />
                  {itemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-terra text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" aria-hidden="true">
                      {itemCount > 99 ? '99+' : itemCount}
                    </span>
                  )}
                </button>
              ) : (
                <Link
                  to="/cart"
                  className="relative p-2 text-sage-800 hover:text-sage-900 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                  aria-label={t('nav:cartWithCount', { count: itemCount })}
                >
                  <ShoppingCart className="h-5 w-5" />
                  {itemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-terra text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" aria-hidden="true">
                      {itemCount > 99 ? '99+' : itemCount}
                    </span>
                  )}
                </Link>
              ))}

              {/* Host-side notification bell — aggregates `rumio:notifications`
                  published by every embedded app (contract v1.10). Hides itself
                  when no app has anything to show, so we don't render a
                  permanently empty bell. Gated to `user` because anonymous
                  visitors don't mount any apps that could publish. */}
              {user && <NotificationBell />}

              {/* Prominent, always-visible booking CTA for lodging tenants.
                  Single-property sites jump straight to the on-page booking
                  widget (#book); multi-property sites go to the stays list to
                  pick a property first. */}
              {IS_LODGING && (
                <Link
                  to={IS_SINGLE_PROPERTY ? '/#book' : '/properties'}
                  className="inline-flex items-center px-4 py-1.5 bg-cta text-cta-fg font-semibold text-sm rounded-full hover:bg-cta-deep transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cta"
                >
                  {t('common:bookNow', { defaultValue: 'Book Now' })}
                </Link>
              )}

              {/* User menu — desktop only. The mobile drawer (hamburger,
                  left) includes the same account links via PR #169, so
                  rendering this avatar dropdown below `lg` produced two
                  parallel nav surfaces. Hide it at the same breakpoint
                  where the hamburger appears (<lg). */}
              {user ? (
                <div className="relative hidden lg:block" ref={userMenuRef}>
                  <button
                    ref={userMenuButtonRef}
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-1 p-2 text-sage-800 hover:text-sage-900 min-w-[44px] min-h-[44px] rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                    aria-label={t('nav:accountMenu')}
                  >
                    <User className="h-5 w-5" />
                    <ChevronDown className="h-3 w-3 hidden sm:block" />
                  </button>

                  {userMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                      <div className="absolute right-0 mt-1 w-48 bg-surface rounded-lg shadow-lg border border-sage-200 z-20 py-1" role="menu">
                        <div className="px-4 py-2 text-sm text-sage-500 border-b border-sage-100">
                          {user.firstName || user.email}
                        </div>
                        <Link to="/account" className="block px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none" role="menuitem" onClick={() => setUserMenuOpen(false)}>{t('nav:myAccount')}</Link>
                        {showEcommerce && (
                          <Link to="/account/orders" className="block px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none" role="menuitem" onClick={() => setUserMenuOpen(false)}>{t('nav:orders')}</Link>
                        )}
                        {showCourses && (
                          <Link to="/account/courses" className="block px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none" role="menuitem" onClick={() => setUserMenuOpen(false)}>{t('nav:myCourses')}</Link>
                        )}
                        {canAccessAdminShell(user.role) && (
                          <Link to={adminLinkDest(user.role)} className="block px-4 py-2 text-sm text-sage-600 font-medium hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none" role="menuitem" onClick={() => setUserMenuOpen(false)}>{t('nav:adminPanel')}</Link>
                        )}
                        {/* Platform-only: let an existing customer spin up an
                            additional site without hunting for the marketing
                            CTA. Routes into the Site Builder onboarding flow. */}
                        {IS_RUMIO && (
                          <Link to="/onboarding" className="block px-4 py-2 text-sm text-sage-600 font-medium hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none" role="menuitem" onClick={() => setUserMenuOpen(false)}>{t('nav:createNewSite')}</Link>
                        )}
                        {visibleAppSections.map((section) => (
                          <div key={section.appId}>
                            <div className="border-t border-sage-100 my-1" aria-hidden />
                            <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wide text-sage-500">
                              {section.appName}
                            </div>
                            {section.items.map((item) => {
                              if (!item.href) return null;
                              const Icon = resolveAppNavIcon(item.icon);
                              return (
                                <Link
                                  key={item.href}
                                  to={item.href}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 focus-visible:bg-sage-50 focus-visible:outline-none"
                                  role="menuitem"
                                  onClick={() => setUserMenuOpen(false)}
                                >
                                  {Icon && <Icon className="h-4 w-4" aria-hidden />}
                                  <span>{item.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        ))}
                        {visibleAppSections.length > 0 && (
                          <div className="border-t border-sage-100 my-1" aria-hidden />
                        )}
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 focus-visible:bg-red-50 focus-visible:outline-none"
                          role="menuitem"
                          onClick={() => { logout(); setUserMenuOpen(false); navigate('/'); }}
                        >
                          {t('nav:signOut')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-sage-800 hover:text-sage-900 border border-sage-600 rounded-lg hover:bg-sage-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                >
                  {t('nav:signIn')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop nav row — hidden for `classic` (nav is inline in the logo row).
          `mega_menu` uses a larger, normal-case treatment; `centered_logo` /
          `transparent_overlay` keep the original compact uppercase style.
          Skipped when there are no links (e.g. inside a replace-mode app mount
          or with hide_nav_when_authed) so it doesn't leave an empty strip. */}
      {!isClassic && navLinks.length > 0 && (
        <nav
          className="hidden lg:block bg-cream border-b border-sage-200"
          aria-label={t('nav:mainNavigation')}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={cn('flex items-center justify-center', isMega ? 'gap-10 h-14' : 'gap-8 h-11')}>
              {navLinks.map((link) =>
                renderDesktopLink(
                  link,
                  cn(
                    'font-sans text-sage-800 hover:text-sage-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded px-1 py-1',
                    isMega ? 'text-sm font-medium' : 'text-xs uppercase tracking-wide-boutique',
                  ),
                ),
              )}
            </div>
          </div>
        </nav>
      )}

    </header>

      {/* Mobile menu — solid slide-in drawer over a dimmed backdrop. Rendered
          as a sibling of <header> (not in-flow) so it overlays page content at
          z-50 with an opaque panel, instead of expanding the sticky header. */}
      {isMobileMenuOpen && (
        <div className="lg:hidden">
          <div
            className="fixed inset-0 z-50 bg-black/50 animate-in fade-in duration-200"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          <nav
            id="mobile-menu"
            className="fixed inset-y-0 right-0 z-50 flex w-[85%] max-w-sm flex-col bg-cream shadow-xl animate-in slide-in-from-right duration-200"
            aria-label={t('nav:mobileNavigation')}
          >
            <div className="flex h-16 flex-none items-center justify-between border-b border-sage-200 px-4">
              <span className="truncate font-display text-lg font-bold text-sage-900">{storeName}</span>
              <button
                type="button"
                onClick={closeMobileMenu}
                className="-mr-2 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-sage-800 hover:text-sage-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                aria-label={t('nav:closeMenu')}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
            {navLinks.map((link) => renderMobileLink(link))}
            {!user && (
              <Link
                to="/login"
                className="block px-3 py-3 text-base font-medium text-sage-600 hover:bg-sage-50 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                onClick={closeMobileMenu}
              >
                {t('nav:signInOrCreate')}
              </Link>
            )}
            {/* User-account links inside the mobile drawer. The avatar dropdown
                in the header is the desktop surface, but on mobile the drawer
                is the more discoverable path — especially when
                `hide_nav_when_authed` is true and the marketing nav is empty,
                which would otherwise leave authed users with a blank panel. */}
            {user && (
              <div className="pt-2 mt-2 border-t border-sage-200 space-y-1">
                <div className="px-3 py-1 text-xs uppercase tracking-wide text-sage-500">
                  {user.firstName || user.email}
                </div>
                <Link
                  to="/account"
                  className="block px-3 py-3 text-base font-medium text-sage-800 hover:bg-sage-50 rounded-lg min-h-[44px] flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                  onClick={closeMobileMenu}
                >
                  {t('nav:myAccount')}
                </Link>
                {showEcommerce && (
                  <Link
                    to="/account/orders"
                    className="block px-3 py-3 text-base font-medium text-sage-800 hover:bg-sage-50 rounded-lg min-h-[44px] flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                    onClick={closeMobileMenu}
                  >
                    {t('nav:orders')}
                  </Link>
                )}
                {showCourses && (
                  <Link
                    to="/account/courses"
                    className="block px-3 py-3 text-base font-medium text-sage-800 hover:bg-sage-50 rounded-lg min-h-[44px] flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                    onClick={closeMobileMenu}
                  >
                    {t('nav:myCourses')}
                  </Link>
                )}
                {canAccessAdminShell(user.role) && (
                  <Link
                    to={adminLinkDest(user.role)}
                    className="block px-3 py-3 text-base font-medium text-sage-700 hover:bg-sage-50 rounded-lg min-h-[44px] flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                    onClick={closeMobileMenu}
                  >
                    {t('nav:adminPanel')}
                  </Link>
                )}
                {IS_RUMIO && (
                  <Link
                    to="/onboarding"
                    className="block px-3 py-3 text-base font-medium text-sage-700 hover:bg-sage-50 rounded-lg min-h-[44px] flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                    onClick={closeMobileMenu}
                  >
                    {t('nav:createNewSite')}
                  </Link>
                )}
                {visibleAppSections.map((section) => (
                  <div key={section.appId} className="pt-1">
                    <div className="border-t border-sage-200 my-1" aria-hidden />
                    <div className="px-3 py-1 text-xs uppercase tracking-wide text-sage-500">
                      {section.appName}
                    </div>
                    {section.items.map((item) => {
                      if (!item.href) return null;
                      const Icon = resolveAppNavIcon(item.icon);
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="flex items-center gap-2 px-3 py-3 text-base font-medium text-sage-800 hover:bg-sage-50 rounded-lg min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                          onClick={closeMobileMenu}
                        >
                          {Icon && <Icon className="h-4 w-4" aria-hidden />}
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ))}
                <button
                  type="button"
                  className="block w-full text-left px-3 py-3 text-base font-medium text-red-600 hover:bg-red-50 rounded-lg min-h-[44px] flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                  onClick={() => { closeMobileMenu(); logout(); navigate('/'); }}
                >
                  {t('nav:signOut')}
                </button>
              </div>
            )}
          </div>
        </nav>
      </div>
    )}
    </>
  );
}
